import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { ApiError, get, post } from "./api";
import type { Permission } from "./permissions";
import type { Principal } from "./types";

/**
 * Who is signed in.
 *
 * The session itself is a `HttpOnly` cookie this code cannot read (spec 9), so
 * there is no client-side token to store and nothing to keep in `localStorage`.
 * "Am I signed in" is answered by asking the server — `GET /auth/me` — which
 * means a revoked session is discovered on the next request rather than trusted
 * until a locally-cached expiry passes.
 */

interface SessionState {
  readonly principal: Principal | undefined;
  /** The first `/auth/me` has not resolved yet; render nothing routable. */
  readonly isResolving: boolean;
  /**
   * Whether the principal holds one permission. The server computes the effective
   * set — roles, direct grants, Administrator's everything — so this is a lookup,
   * never a rule; a control it hides is one the API would refuse anyway.
   */
  readonly can: (permission: Permission) => boolean;
  signIn: (credentials: { email: string; password: string }) => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionState | undefined>(undefined);

export const ME_KEY = ["auth", "me"] as const;

export function SessionProvider({ children }: { readonly children: ReactNode }) {
  const queryClient = useQueryClient();

  const me = useQuery({
    queryKey: ME_KEY,
    queryFn: () => get<Principal>("/auth/me"),
    /**
     * A 401 is the expected answer for a visitor who is not signed in, not a
     * transient fault. Retrying it three times delays the login screen by a
     * second for everyone who is simply logged out.
     */
    retry: (failureCount, error) =>
      !(error instanceof ApiError && error.isUnauthenticated) && failureCount < 2,
    staleTime: 60_000,
  });

  const signInMutation = useMutation({
    mutationFn: (credentials: { email: string; password: string }) =>
      post<Principal>("/auth/login", credentials),
    onSuccess: (principal) => {
      // Seeded rather than invalidated: the login response carries the same
      // principal `/auth/me` would return, so refetching it immediately would be
      // a second round trip for an answer already in hand.
      queryClient.setQueryData(ME_KEY, principal);
    },
  });

  const signOutMutation = useMutation({
    mutationFn: () => post<{ loggedOut: boolean }>("/auth/logout"),
    /**
     * `onSettled`, not `onSuccess`. If the logout request fails the local session
     * must still be discarded — leaving an operator apparently signed in after
     * they pressed sign out is the wrong failure direction, and the cookie is
     * revoked server-side or it is not, independent of what this cache holds.
     */
    onSettled: () => {
      queryClient.clear();
    },
  });

  const signIn = useCallback(
    async (credentials: { email: string; password: string }) => {
      await signInMutation.mutateAsync(credentials);
    },
    [signInMutation],
  );

  const signOut = useCallback(async () => {
    await signOutMutation.mutateAsync().catch(() => undefined);
  }, [signOutMutation]);

  const principal = me.data;

  const value = useMemo<SessionState>(
    () => ({
      principal,
      // `isLoading` is false once a 401 has resolved, which is what lets the
      // login screen render instead of a spinner that never clears.
      isResolving: me.isLoading,
      can: (permission) => principal !== undefined && principal.permissions.includes(permission),
      signIn,
      signOut,
    }),
    [principal, me.isLoading, signIn, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error("useSession must be used inside a SessionProvider");
  }
  return context;
}
